<template>
  <div class="pos-relative">
    <SectionPageTitle
      title="Users"
      :actions="[{ method: openCreate, label: '+ Add User' }]"
    />

    <div class="w-100 pd-medium pos-relative">
      <Feed
        :search="true"
        :showLoadMore="false"
        :states="{
          empty: {
            title: 'No Users Found',
            description: 'No users match your search criteria.'
          }
        }"
        :store="{
          read: (options) => users.actions.read(options),
          state: users.state
        }"
        :options="{
          limit: 20,
          search: route.query.search
        }"
        v-slot="{ items }"
        class="cols-1 pos-relative w-100 gap-thin"
      >
        <div
          v-for="item in items"
          :key="item._id"
          @click="openEdit(item)"
          class="pd-small bg-card radius-medium cursor-pointer hover-bg-base h-4r"
        >
          <CardUser
            :user="item"
            :photo="item.profile?.photo"
            :name="item.profile?.name || 'Anonymous'"
            :email="item.email || item.phone || item.username || '—'"
            :role="item.roles"
            :status="item.status"
            :statusClass="item.status === 'active' ? 'bg-accent-transp-20 t-accent' : 'bg-negative-transp-20 t-negative'"
          />
        </div>
      </Feed>
    </div>

    <!-- Create User Popup -->
    <Popup
      :isPopupOpen="isCreateOpen"
      @close-popup="closeCreate"
      title="Create User"
      class="w-m-50r mobile:h-100 bg-base mobile:radius-zero radius-medium pd-medium"
    >
      <div class="for-transition w-100">
        <Field
          v-model:field="createData.email"
          label="Email"
          placeholder="user@example.com"
          class="mn-b-thin w-100 bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="createData.phone"
          label="Phone"
          placeholder="+66..."
          class="mn-b-thin w-100 bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="createData.username"
          label="@"
          placeholder="username"
          class="mn-b-thin w-100 bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="createData.profile.name"
          label="Name"
          placeholder="Full name"
          class="mn-b-thin w-100 bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="createData.password"
          label="Password"
          placeholder="Password"
          type="password"
          class="mn-b-semi w-100 bg-card pd-medium radius-small"
        />

        <h4 class="mn-b-small">Roles</h4>

        <div class="flex flex-wrap gap-thin mn-b-semi">
          <div
            v-for="role in availableRoles"
            :key="role._id"
            @click="toggleCreateRole(role)"
            class="pd-l-small pd-r-small pd-t-thin pd-b-thin radius-small cursor-pointer fw-medium capitalize"
            :class="createData.roles.includes(role._id) ? 'bg-accent-secondary t-inverse' : 'bg-card t-secondary'"
          >
            {{ role.name }}
          </div>
        </div>

        <Button
          :submit="createUser"
          :callback="closeCreate"
          class="mn-b-thin bg-accent w-100"
        >
          Create
        </Button>
      </div>
    </Popup>

    <!-- Edit User Popup -->
    <Popup
      :isPopupOpen="isPopupOpen"
      @close-popup="closeEdit"
      title="Edit User"
      class="w-m-50r mobile:h-100 bg-base mobile:radius-zero radius-medium pd-medium"
    >
      <div v-if="editData" class="for-transition w-100">
        <div class="mn-b-semi">
          <div class="mn-b-thin radius-small w-100 h-10r bg-card flex-center flex-column flex">
            <img
              v-if="editData.profile?.photo"
              :src="(FILE_SERVER_URL || '') + editData.profile.photo"
              class="radius-extra aspect-1x1 object-fit-cover h-4r w-4r mn-b-thin"
            />
            <PlaceholderUserpic
              v-else
              class="radius-extra h-4r w-4r mn-b-thin"
            />
            <p class="p-small opacity-50">{{ editData.email || editData.phone || '' }}</p>
          </div>
        </div>

        <h4 class="mn-b-small">Profile</h4>

        <Field
          v-model:field="editData.username"
          label="@"
          placeholder="Not specified"
          class="mn-b-thin w-100 bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="editData.profile.name"
          label="Name"
          placeholder="Not specified"
          class="mn-b-thin bg-card pd-medium radius-small"
        />

        <Field
          v-model:field="editData.profile.description"
          label="Description"
          placeholder="Not specified"
          class="mn-b-semi bg-card pd-medium radius-small"
        />

        <h4 class="mn-b-small">Status</h4>

        <Select
          v-model:select="editData.status"
          :options="statusOptions"
          placeholder="Select status"
          class="mn-b-semi bg-card pd-medium radius-small"
        />

        <h4 class="mn-b-small">Roles</h4>

        <div class="flex flex-wrap gap-thin mn-b-semi">
          <div
            v-for="role in availableRoles"
            :key="role._id"
            @click="toggleRole(role)"
            class="pd-l-small pd-r-small pd-t-thin pd-b-thin radius-small cursor-pointer fw-medium capitalize"
            :class="isRoleSelected(role) ? 'bg-accent-secondary t-inverse' : 'bg-card t-secondary'"
          >
            {{ role.name }}
          </div>
        </div>

        <Button
          :submit="saveUser"
          :callback="closeEdit"
          class="mn-b-thin bg-accent w-100"
        >
          Save
        </Button>
      </div>
    </Popup>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted } from 'vue'

  import { useRoute } from 'vue-router'

  import Feed from '@martyrs/src/design-system/components/Feed/Feed.vue'
  import Field from '@martyrs/src/design-system/components/Field/Field.vue'
  import Button from '@martyrs/src/design-system/components/Button/Button.vue'
  import Select from '@martyrs/src/design-system/components/Select/Select.vue'
  import Popup from '@martyrs/src/design-system/components/Popup/Popup.vue'
  import SectionPageTitle from '@martyrs/src/modules/core/views/components/sections/SectionPageTitle.vue'
  import CardUser from '@martyrs/src/modules/core/views/components/blocks/CardUser.vue'
  import PlaceholderUserpic from '@martyrs/src/modules/icons/placeholders/PlaceholderUserpic.vue'

  import * as users from '@martyrs/src/modules/auth/views/store/users.js'
  import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'

  const route = useRoute()

  const isPopupOpen = ref(false)
  const editData = ref(null)
  const editSourceItem = ref(null)
  const availableRoles = ref([])

  const isCreateOpen = ref(false)
  const createData = reactive({
    email: '',
    phone: '',
    username: '',
    password: '',
    roles: [],
    profile: {
      name: '',
    },
  })

  const openCreate = () => {
    createData.email = ''
    createData.phone = ''
    createData.username = ''
    createData.password = ''
    createData.roles = []
    createData.profile.name = ''
    isCreateOpen.value = true
  }

  const closeCreate = () => {
    isCreateOpen.value = false
  }

  const toggleCreateRole = (role) => {
    const idx = createData.roles.indexOf(role._id)
    if (idx > -1) {
      createData.roles.splice(idx, 1)
    } else {
      createData.roles.push(role._id)
    }
  }

  const createUser = async () => {
    const payload = {}
    if (createData.email) payload.email = createData.email
    if (createData.phone) payload.phone = createData.phone
    if (createData.username) payload.username = createData.username
    if (createData.password) payload.password = createData.password
    if (createData.roles.length) payload.roles = createData.roles
    if (createData.profile.name) payload.profile = { name: createData.profile.name }

    const result = await users.actions.create(payload)
    if (result) {
      users.state.all.unshift(result)
    }
  }

  const statusOptions = ['active', 'banned', 'inactive']

  const openEdit = (user) => {
    editSourceItem.value = user
    editData.value = {
      _id: user._id,
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      status: user.status || 'active',
      roles: (user.roles || []).map(r => r._id || r),
      profile: {
        name: user.profile?.name || '',
        description: user.profile?.description || '',
        photo: user.profile?.photo || '',
      },
    }
    isPopupOpen.value = true
  }

  const closeEdit = () => {
    isPopupOpen.value = false
    editData.value = null
    editSourceItem.value = null
  }

  const toggleRole = (role) => {
    const idx = editData.value.roles.indexOf(role._id)
    if (idx > -1) {
      editData.value.roles.splice(idx, 1)
    } else {
      editData.value.roles.push(role._id)
    }
  }

  const isRoleSelected = (role) => {
    return editData.value?.roles.includes(role._id)
  }

  const saveUser = async () => {
    if (!editData.value) return
    const { data } = await $axios.put(`/api/users/${editData.value._id}/admin`, {
      profile: editData.value.profile,
      username: editData.value.username,
      status: editData.value.status,
      roles: editData.value.roles,
    })

    // Update list item from server response
    if (data && editSourceItem.value) {
      editSourceItem.value.profile = data.profile || editSourceItem.value.profile
      editSourceItem.value.username = data.username
      editSourceItem.value.status = data.status
      editSourceItem.value.roles = data.roles || []
    }
  }

  onMounted(async () => {
    try {
      const { data } = await $axios.get('/api/roles')
      availableRoles.value = data
    } catch (err) {
      console.error('Failed to load roles:', err)
    }
  })
</script>
